export const parseApplications = (data) => {
  if (!data) return [];

  // Handle plain object
  if (typeof data === "object" && !Array.isArray(data)) {
    return Object.values(data).map((datum) => parseApplication(datum));
  }

  // Handle plain array
  if (Array.isArray(data)) {
    return data.map((datum) => parseApplication(datum));
  }

  return [];
};

export const parseApplication = (application) => {
  if (!application)
    return {
      subscriptions: [],
      subscriptionIds: [],
      products: [],
      clientId: {},
      applicationStatus: "",
      clientSecret: "",
    };

  // Handle both old subscriptions and new products structure
  const subscriptionIds = getSubscriptionIds(application);
  const subscriptions = parseSubscriptions(application);
  const products = parseProducts(application);

  // Handle allowedGrantTypes
  let allowedGrantTypes = [];
  const grantTypes = application.allowedGrantTypes;
  if (grantTypes) {
    if (Array.isArray(grantTypes)) {
      allowedGrantTypes = grantTypes;
    } else if (typeof grantTypes === "object") {
      allowedGrantTypes = Object.values(grantTypes);
    }
  }

  return {
    created: application.created,
    nid: application.nid,
    uuid: application.uuid || application.id,
    title: application.title || application.appName,
    postLogoutRedirectUri:
      application.postLogoutRedirectUri || application.postlogoutRedirectUri,
    oauthRedirectUri: application.oauthRedirectUri,
    allowedGrantTypes,
    allowedGrantTypesWithIds: application.allowedGrantTypesWithIds || [],
    image: application.image || application.appImage,
    // The mapper wraps the description as { value, processed }. An empty value must stay "",
    // not fall back to the wrapper object: the edit form then showed "[object Object]" and
    // Yup refused the save ("description must be a `string` type").
    descriptionValue:
      application.description && typeof application.description === "object"
        ? application.description.value ?? ""
        : application.description ?? "",
    descriptionProcessed: application.description?.processed,
    subscriptionIds,
    subscriptions,
    products,
    clientId: {
      development: application.clientId?.development || application.clientIdDev,
      production: application.clientId?.production || application.clientIdProd,
    },
    clientIdDev: application.clientIdDev,
    applicationStatus:
      application.applicationStatus ||
      (application.appStatus?.name?.toUpperCase() === "PRODUCTION"
        ? "PRODUCTION"
        : "DEVELOPMENT") ||
      application.appStatus,
    clientSecret:
      application.clientSecret ||
      application.clientSecretDev ||
      application.clientSecretProd,
    organizationName: application.organizationName,
    organizationId: application.organizationId,
  };
};

export const parseApplicationFromState = (state, applicationId) => {
  const application =
    state.applicationsById && state.applicationsById[applicationId];
  return parseApplication(application);
};

export const parseSubscriptions = (application) => {
  if (!application) return [];

  // First check for subscriptions field
  const subs = application.subscriptions;
  if (subs) {
    if (Array.isArray(subs)) return subs;
    if (typeof subs === "object") return Object.values(subs);
  }

  // If no subscriptions but has products, treat products as subscriptions
  // This handles the new API structure where products are the subscriptions
  const products = application.products;
  if (products && Array.isArray(products)) {
    // Map products to subscription format with uuid field
    return products.map((product) => ({
      uuid: product.id || product.uuid,
      ...product,
    }));
  }

  return [];
};

export const parseProducts = (application) => {
  if (!application) return [];

  const products = application.products;
  if (!products) return [];
  if (Array.isArray(products)) return products;
  if (typeof products === "object") return Object.values(products);
  return [];
};

export const getSubscriptionIds = (application) => {
  if (!application) return [];

  // Handle both subscriptionIds and subscriptionsIds for backward compatibility
  const ids = application.subscriptionIds || application.subscriptionsIds;
  if (Array.isArray(ids)) {
    return ids;
  } else if (ids && typeof ids === "object") {
    return Object.values(ids);
  }

  // If no subscriptionIds but has products, extract product IDs
  const products = parseProducts(application);
  if (products.length > 0) {
    return products.map((p) => p.id || p.uuid).filter(Boolean);
  }

  return [];
};
